import { Box, Divider, useTheme } from '@mui/material';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import FlycasterAvatar from '@components/FlycasterAvatar';
import { NAVBAR_VIEWS, RESET_VIEW_CONFIG, ROUTES } from '@const';
import {
  openPanel,
  openPanelForTab,
  resetMap,
  setActivePage,
  setDetailPanelActiveTab,
  toggleDataminrData,
  toggleEgeData,
  toggleOffAllLayers,
  togglePanelCampaignCreator,
  toggleSenpaiDetections,
  toggleWIFIData
} from '@slice';
import { RootState } from '@store';
import { NavbarOption } from '@types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import CampaignButton from '../ButtonBarV2/Buttons/CampaignButton';
import ShareButton from '../ButtonBarV2/Buttons/ShareButton';
import NavbarButton from './NavbarButton';
import NavbarMailbox from './NavbarMailbox';
import NavbarScrollSection from './NavbarScrollSection';
import TouchstoneLayerPopover from './TouchstoneLayerPopover';

const Navbar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const spacerRef = useRef<HTMLDivElement>(null);

  const {
    user,
    activePage,
    polygonMode,
    isEgeDataOn,
    isWIFIDataOn,
    senpaiDataOn,
    dataminrDataOn,
    campaignH3DwellLayerEnabled,
    isPanelCampaignCreatorOpen,
    detailPanelActiveTab
  } = useSelector(
    (state: RootState) => ({
      user: state.slice.user,
      activePage: state.slice.activePage,
      polygonMode: state.slice.polygonMode,
      isEgeDataOn: state.slice.isEgeDataOn,
      isWIFIDataOn: state.slice.isWIFIDataOn,
      senpaiDataOn: state.slice.senpaiDataOn,
      dataminrDataOn: state.slice.dataminrDataOn,
      campaignH3DwellLayerEnabled: state.slice.campaignH3DwellLayerEnabled,
      isPanelCampaignCreatorOpen: state.slice.isPanelCampaignCreatorOpen,
      detailPanelActiveTab: state.slice.detailPanelActiveTab
    }),
    shallowEqual
  );

  const handleReset = useCallback(() => {
    dispatch(resetMap());
    if (activePage !== ROUTES.HOME) {
      navigate(ROUTES.HOME);
    }
  }, [dispatch, activePage, navigate]);

  const onViewChange = useCallback(
    (view: NavbarOption) => {
      // When navigating away from the map, turn off polygon and wifi layers
      if (view.path !== ROUTES.HOME) {
        dispatch(toggleOffAllLayers());
        dispatch(setDetailPanelActiveTab('touchstone'));
      }

      if (view.path) {
        dispatch(setActivePage(view.path));
        navigate(view.path);
      }
    },
    [navigate, dispatch]
  );

  const onToggle = useCallback(
    (view: NavbarOption) => {
      if (view.toggleAction) {
        switch (view.toggleAction) {
          case 'toggleWIFIData':
            dispatch(toggleWIFIData());
            break;
          case 'toggleEgeData':
            dispatch(toggleEgeData());
            break;
          case 'toggleARIData':
            dispatch(toggleSenpaiDetections());
            break;
          case 'toggleDataminrData':
            dispatch(toggleDataminrData());
            break;
          case 'toggleTitoData':
            dispatch(setDetailPanelActiveTab('tito'));
            dispatch(openPanel());
            if (isPanelCampaignCreatorOpen) {
              dispatch(togglePanelCampaignCreator());
            }
            break;
        }
      }

      // Navigate to home if not already there
      if (activePage !== ROUTES.HOME) {
        dispatch(setActivePage(ROUTES.HOME));
        navigate(ROUTES.HOME);
      }

      // Auto-open panel for tabs with hasTab (only if never manually closed)
      if (view.hasTab) {
        dispatch(openPanelForTab());
      }
    },
    [activePage, dispatch, isPanelCampaignCreatorOpen, navigate]
  );

  const getToggleState = useCallback(
    (view: string): boolean => {
      // Optional, because this is now called with a NAME rather than only with keys the
      // config was just iterated over. A view that is absent -- renamed, feature-flagged
      // off, or missing from a test's mock -- should read as "not toggled", not take the
      // whole navbar down with it.
      switch (NAVBAR_VIEWS[view]?.toggleAction) {
        case 'toggleWIFIData':
          return isWIFIDataOn;
        case 'toggleARIData':
          return senpaiDataOn;
        case 'toggleDataminrData':
          return dataminrDataOn;
        default:
          return false;
      }
    },
    [isWIFIDataOn, senpaiDataOn, dataminrDataOn]
  );

  const isTouchstoneActive = polygonMode !== 'off' || isEgeDataOn || campaignH3DwellLayerEnabled;

  const popoverToggleStates = {
    toggleTitoData: detailPanelActiveTab === 'tito'
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(1, 0),
        gap: theme.spacing(2),
        width: '69px',
        minHeight: 0,
        borderRight: '1px solid var(--divider, rgba(0, 0, 0, 0.12))'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          gap: 1,
          flex: '0 1 auto',
          minHeight: 0
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          <NavbarButton
            boldTextOnActive
            backgroundActive
            viewConfig={RESET_VIEW_CONFIG}
            onClick={handleReset}
            activePage={activePage}
            isActive={activePage === ROUTES.HOME}
            iconTextGap="5px"
            label={
              <>
                <span style={{ fontWeight: 400 }}>SEnP</span>
                <span style={{ fontWeight: 700 }}>AI</span>
              </>
            }
          />
        </Box>

        <Divider sx={{ width: '38px' }} />

        <NavbarScrollSection spacerRef={spacerRef}>
          <NavbarButton
            showBadge
            view="touchstone"
            activePage={activePage}
            onToggle={onToggle}
            isActive={isTouchstoneActive}
            // The name comes from the config rather than a literal, so it cannot drift
            // from the tooltips and the popover title beside it.
            //
            // WEIGHT ONLY, never colour. This button passes no `boldTextOnActive`, so
            // its label stays 400/text.secondary in both states -- the 700 here just
            // makes the `+` read as part of the name rather than punctuation after it.
            // Colour is left to inherit so the superscript can never be stranded in a
            // different colour from the name it belongs to.
            label={
              <>
                {NAVBAR_VIEWS.touchstone.displayName}
                <sup style={{ fontWeight: 700 }}>+</sup>
              </>
            }
            renderPopover={(anchorEl, open, onClose) => <TouchstoneLayerPopover anchorEl={anchorEl} open={open} onClose={onClose} />}
          />

          {Object.keys(NAVBAR_VIEWS)
            .filter((view) => NAVBAR_VIEWS[view].group === 'main' && NAVBAR_VIEWS[view].enabled && NAVBAR_VIEWS[view].showInNavbar !== false)
            .map((view) => (
              <NavbarButton
                key={view}
                view={view}
                activePage={activePage}
                onViewChange={onViewChange}
                onToggle={onToggle}
                isActive={getToggleState(view)}
                popoverToggleStates={popoverToggleStates}
              />
            ))}
        </NavbarScrollSection>

        <Divider sx={{ width: '38px' }} />

        {/* Tacklebox and Stats sit together with no divider or gap between them. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            flexShrink: 0
          }}
        >
          <NavbarButton
            showBadge
            boldTextOnActive
            view="tacklebox"
            activePage={activePage}
            onViewChange={onViewChange}
            onToggle={onToggle}
            isActive={detailPanelActiveTab === 'tito'}
            popoverToggleStates={popoverToggleStates}
          />

          <NavbarButton
            boldTextOnActive
            view="stats"
            activePage={activePage}
            onViewChange={onViewChange}
            onToggle={onToggle}
            isActive={activePage === ROUTES.STATS}
            popoverToggleStates={popoverToggleStates}
          />
        </Box>
      </Box>

      <Box ref={spacerRef} sx={{ flex: 1 }} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          alignSelf: 'stretch'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          <ShareButton />
          <CampaignButton />
        </Box>

        <Divider sx={{ width: '38px' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'stretch'
          }}
        >
          <NavbarMailbox />
          <FlycasterAvatar user={user.email} height={48} />
        </Box>
      </Box>
    </Box>
  );
};

export default Navbar;
